import readlangClient from "readlangClient";
import * as dateUtils from "shared/dateUtils";

/** How long a measured client/server clock offset is trusted for */
const HOUR = 3600 * 1000;
let maxAnchorAge = HOUR;

/**
 * Give up on a slow /api/currentDate rather than leaving callers hanging -
 * readlangClient leaves its promise pending forever on connection loss and
 * server errors, and a stalled request never settles at all.
 */
const DEFAULT_REQUEST_TIMEOUT = 10 * 1000;
let requestTimeout = DEFAULT_REQUEST_TIMEOUT;

/** A server time together with the client time at which it was measured */
let anchor: { server: Date; client: Date } | undefined;
let refreshInFlight: Promise<void> | undefined;

if (window.READLANG.serverTime) {
  anchor = { server: new Date(window.READLANG.serverTime), client: new Date() };
}

// 'snaps' the date to the previous hour specified (based on local timezone)
export const getSnapped = async (snapHour): Promise<Date> => {
  const date = await get();
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate() - (date.getHours() < snapHour ? 1 : 0),
    snapHour
  );
};

export const get = async (): Promise<Date> => {
  if (!anchor) {
    await refresh();
    return estimate();
  }

  const anchorAge = new Date().getTime() - anchor.client.getTime();
  if (anchorAge < 0 || anchorAge > maxAnchorAge) {
    // Re-sync in the background. Callers advance flashcards on this path, so
    // they must never be held up by the network for a date we can already
    // estimate to within a few milliseconds.
    void refresh();
  }

  return estimate();
};

/** The client clock corrected by the last measured offset from the server */
const estimate = () => {
  const offset = anchor
    ? anchor.server.getTime() - anchor.client.getTime()
    : /* nothing to correct by, so trust the client clock */ 0;
  return new Date(new Date().getTime() + offset);
};

/** Re-measures the clock offset, sharing one request between concurrent calls */
const refresh = async () => {
  refreshInFlight ??= fetchAnchor();
  await refreshInFlight;
  refreshInFlight = undefined;
};

/** Never throws - an unreachable server falls back to the client clock */
const fetchAnchor = async () => {
  let server: Date;
  try {
    const response = await withTimeout(
      readlangClient.request<{ currentDate: string }>(
        "GET",
        "/api/currentDate",
        {}
      )
    );
    if (!response.data?.currentDate) {
      throw new Error("no date in response");
    }
    server = new Date(dateUtils.parse(response.data.currentDate));
  } catch (error) {
    console.error("Couldn't get the current date from the server:", error);
    // Re-anchor on the estimate anyway, so a server we can't reach doesn't mean
    // a request per call
    server = estimate();
  }
  anchor = { server, client: new Date() };
};

/** Rejects if the request hasn't settled within requestTimeout */
const withTimeout = <T>(request: Promise<T>) =>
  new Promise<T>((resolve, reject) => {
    const timeoutID = setTimeout(
      () => reject(new Error("timed out")),
      requestTimeout
    );
    request.then(
      (value) => {
        clearTimeout(timeoutID);
        resolve(value);
      },
      (error) => {
        clearTimeout(timeoutID);
        reject(error);
      }
    );
  });

export const _functionsForUnitTests = {
  /**
   * Forgets the measured clock offset so the next get() goes to the network.
   * The timings are overridable so tests don't have to wait out the real ones.
   */
  reset: ({
    requestTimeout: timeout = DEFAULT_REQUEST_TIMEOUT,
    anchorAgeLimit = HOUR,
  } = {}) => {
    anchor = undefined;
    refreshInFlight = undefined;
    requestTimeout = timeout;
    maxAnchorAge = anchorAgeLimit;
  },
};
